// The OAuth consent round trip in a popup, so the page that started it never
// leaves. The popup is opened SYNCHRONOUSLY on the click (popup blockers allow
// that) and pointed at the consent URL once the server answers; the callback
// ends on a page that posts {type: "hostit:consent", result} to us and closes.
// A popup that closes without a word (mobile made it a tab, a provider
// redirected with noopener) resolves with ok null: the caller re-fetches.
//
// The popup keeps window.opener (that is how it reports back), so every page
// the consent passes through could navigate this tab. That is why only the
// welcome tour uses this, for the operator's own suggested providers and
// servers; the server refuses popup: true for anything else, and the
// Connections page keeps the full-page consent.
export const CONSENT_MESSAGE = "hostit:consent";
const POLL_MS = 500;

// consentResult reads a message event: the popup's result, or null for any
// other message (wrong origin, wrong shape). Pure, so it is tested alone.
export function consentResult(event, origin) {
  if (!event || event.origin !== origin) return null;
  const d = event.data;
  if (!d || d.type !== CONSENT_MESSAGE || !d.result || typeof d.result !== "object") return null;
  return { ok: d.result.ok === true, slug: typeof d.result.slug === "string" ? d.result.slug : "" };
}

// openConsent runs `start` (a request that answers {redirect_url} for a consent
// or a finished connection otherwise) with a popup ready for the consent.
// Resolves {ok, slug} from the popup, {ok: true, created} when `start` made the
// connection without a consent, or {ok: null} when the popup vanished silently.
// Rejects with `start`'s error (the popup is closed first).
export async function openConsent(start) {
  const popup = window.open("", "hostit-consent", "popup,width=600,height=720");
  let res;
  try {
    res = await start();
  } catch (err) {
    if (popup) popup.close();
    throw err;
  }
  if (!res || !res.redirect_url) {
    if (popup) popup.close();
    return { ok: true, created: res };
  }
  if (!popup) {
    // Blocked after all: fall back to the full-page consent.
    window.location.href = res.redirect_url;
    return new Promise(() => {});
  }
  popup.location.href = res.redirect_url;
  return new Promise((resolve) => {
    const done = (result) => {
      window.removeEventListener("message", onMessage);
      clearInterval(timer);
      resolve(result);
    };
    const onMessage = (e) => {
      const result = consentResult(e, window.location.origin);
      if (result) done(result);
    };
    window.addEventListener("message", onMessage);
    const timer = setInterval(() => {
      if (popup.closed) done({ ok: null });
    }, POLL_MS);
  });
}
